<template>
  <div>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    :close-on-content-click="false"
    max-width="420"
    content-class="lk-notifications-menu"
    @input="onMenuToggle"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        class="lk-notifications-bell mr-2"
        v-bind="attrs"
        v-on="on"
        aria-label="Уведомления"
      >
        <v-badge
          :content="badgeCount > 99 ? '99+' : badgeCount"
          :value="badgeCount > 0"
          color="error"
          overlap
        >
          <v-icon
            :class="['lk-notifications-bell__icon', { 'lk-notifications-bell__icon--shake': shakeActive }]"
            color="#FFC107"
          >
            mdi-bell
          </v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card min-width="380" max-height="520" class="lk-notifications-panel" elevation="8">
      <div class="lk-notifications-panel__header">
        <div class="d-flex align-center">
          <v-icon color="#FFC107" class="mr-2">mdi-bell-ring-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Уведомления</span>
          <v-chip
            v-if="unreadCount > 0"
            x-small
            color="error"
            text-color="white"
            class="ml-2 font-weight-medium"
          >
            {{ unreadCount }}
          </v-chip>
        </div>
        <v-btn
          v-if="unreadCount > 0"
          small
          rounded
          outlined
          color="primary"
          class="text-none lk-notifications-panel__read-all"
          :loading="markingAll"
          @click.stop="markAllRead"
        >
          <v-icon left small>mdi-check-all</v-icon>
          Прочитать все
        </v-btn>
      </div>
      <v-divider />

      <div class="lk-notifications-panel__body">
        <div v-if="activeJobs.length" class="lk-notifications-panel__section">
          <div class="lk-notifications-panel__section-title">
            <v-icon x-small class="mr-1">mdi-progress-upload</v-icon>
            Фоновые загрузки
          </div>
          <div
            v-for="job in activeJobs"
            :key="'job-' + (job.mediaKind || 'audio') + '-' + job.mass_upload_id"
            class="lk-notifications-panel__card lk-notifications-panel__card--job"
          >
            <div class="lk-job-card">
              <div class="lk-job-card__head">
                <div class="lk-job-card__title">
                  {{ jobTitle(job) }}: {{ job.processed }} / {{ job.total }}
                </div>
                <v-btn
                  v-if="job.can_cancel || job.status === 'queued'"
                  x-small
                  text
                  color="error"
                  class="text-none lk-job-card__cancel"
                  :loading="!!cancellingJobs[job.mass_upload_id]"
                  @click.stop="askCancelJob(job)"
                >
                  <v-icon left x-small>mdi-close-circle-outline</v-icon>
                  Отменить
                </v-btn>
              </div>
              <div v-if="job.file_name || job.mass_upload_id" class="caption grey--text mb-1">
                <span v-if="job.file_name">{{ job.file_name }}</span>
                <span v-if="job.file_name && job.mass_upload_id"> · </span>
                <span v-if="job.mass_upload_id">{{ job.mass_upload_id }}</span>
              </div>
              <v-progress-linear
                v-if="job.status !== 'queued'"
                :value="job.percent"
                height="8"
                rounded
                color="primary"
                class="my-2"
              />
              <v-progress-linear
                v-else
                indeterminate
                height="8"
                rounded
                color="orange"
                class="my-2"
              />
              <div class="caption grey--text">
                <template v-if="job.status === 'queued'">
                  <template v-if="job.start_date">
                    В очереди · загрузка {{ formatDate(job.start_date) }}
                    <template v-if="job.end_date">, сброс {{ formatDate(job.end_date) }}</template>
                  </template>
                  <template v-else>
                    В очереди · начнётся после завершения предыдущих загрузок
                  </template>
                </template>
                <template v-else>
                  Выполняется · {{ job.success }} ок / {{ job.failed }} ошибок
                </template>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="lk-notifications-panel__loader">
          <v-progress-circular indeterminate color="primary" size="28" width="3" />
          <span class="caption grey--text mt-2">Загрузка…</span>
        </div>
        <template v-else-if="items.length">
          <div v-if="unreadItems.length" class="lk-notifications-panel__section">
            <div class="lk-notifications-panel__section-title">
              <v-icon x-small class="mr-1">mdi-circle</v-icon>
              Новые
            </div>
            <div
              v-for="item in unreadItems"
              :key="'u-' + item.id"
              class="lk-notifications-panel__card lk-notifications-panel__card--unread"
              :style="unreadCardStyle(item.type)"
              @click="openNotification(item)"
            >
              <div class="lk-notification-card">
                <div
                  class="lk-notification-card__avatar"
                  :style="typeAvatarStyle(item.type)"
                >
                  <v-icon small :color="typeColor(item.type)">{{ typeIcon(item.type) }}</v-icon>
                </div>
                <div class="lk-notification-card__body">
                  <div class="lk-notification-card__top">
                    <span class="lk-notification-card__title">{{ item.title }}</span>
                    <span
                      class="lk-notification-card__dot"
                      title="Непрочитано"
                      :style="{ background: typeColor(item.type) }"
                    />
                  </div>
                  <v-chip
                    v-if="typeLabel(item.type)"
                    x-small
                    label
                    outlined
                    :color="typeChipColor(item.type)"
                    class="lk-notification-card__type mt-1"
                  >
                    <v-icon left x-small>{{ typeIcon(item.type) }}</v-icon>
                    {{ typeLabel(item.type) }}
                  </v-chip>
                  <p class="lk-notification-card__text mb-0">{{ displayBody(item.body) }}</p>
                  <div v-if="showMassUploadActions(item)" class="mt-2 d-flex align-center" @click.stop>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="mr-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openMassResult(massUploadMetaFromBody(item.body).id, massUploadMetaFromBody(item.body).kind)"
                        >
                          <v-icon small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span>Открыть результат</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="mr-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadMassAudio(massUploadMetaFromBody(item.body).id, massUploadMetaFromBody(item.body).kind)"
                        >
                          <v-icon small>
                            {{ massUploadMetaFromBody(item.body).kind === 'lcd' ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ massUploadMetaFromBody(item.body).kind === 'lcd' ? 'Скачать изображение' : 'Скачать аудио' }}
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadMassResult(massUploadMetaFromBody(item.body).id)"
                        >
                          <v-icon small>mdi-file-delimited-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Скачать CSV</span>
                    </v-tooltip>
                  </div>
                  <span class="lk-notification-card__date">{{ formatDate(item.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="readItems.length" class="lk-notifications-panel__section">
            <div class="lk-notifications-panel__section-title lk-notifications-panel__section-title--read">
              Прочитанные
              <span class="lk-notifications-panel__section-count">({{ readItems.length }})</span>
            </div>
            <div
              v-for="item in paginatedReadItems"
              :key="'r-' + item.id"
              class="lk-notifications-panel__card"
              @click="openNotification(item)"
            >
              <div class="lk-notification-card">
                <div
                  class="lk-notification-card__avatar"
                  :style="typeAvatarStyle(item.type)"
                >
                  <v-icon small :color="typeColor(item.type)">{{ typeIcon(item.type) }}</v-icon>
                </div>
                <div class="lk-notification-card__body">
                  <div class="lk-notification-card__top">
                    <span class="lk-notification-card__title">{{ item.title }}</span>
                  </div>
                  <v-chip
                    v-if="typeLabel(item.type)"
                    x-small
                    label
                    outlined
                    :color="typeChipColor(item.type)"
                    class="lk-notification-card__type mt-1"
                  >
                    <v-icon left x-small>{{ typeIcon(item.type) }}</v-icon>
                    {{ typeLabel(item.type) }}
                  </v-chip>
                  <p class="lk-notification-card__text mb-0">{{ displayBody(item.body) }}</p>
                  <div v-if="showMassUploadActions(item)" class="mt-2 d-flex align-center" @click.stop>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="mr-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openMassResult(massUploadMetaFromBody(item.body).id, massUploadMetaFromBody(item.body).kind)"
                        >
                          <v-icon small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </template>
                      <span>Открыть результат</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="mr-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadMassAudio(massUploadMetaFromBody(item.body).id, massUploadMetaFromBody(item.body).kind)"
                        >
                          <v-icon small>
                            {{ massUploadMetaFromBody(item.body).kind === 'lcd' ? 'mdi-file-image-outline' : 'mdi-file-music-outline' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ massUploadMetaFromBody(item.body).kind === 'lcd' ? 'Скачать изображение' : 'Скачать аудио' }}
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadMassResult(massUploadMetaFromBody(item.body).id)"
                        >
                          <v-icon small>mdi-file-delimited-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Скачать CSV</span>
                    </v-tooltip>
                  </div>
                  <span class="lk-notification-card__date">{{ formatDate(item.created_at) }}</span>
                </div>
              </div>
            </div>
            <div
              v-if="readPageCount > 1"
              class="lk-notifications-panel__pagination"
            >
              <v-btn
                icon
                x-small
                :disabled="readPage <= 1"
                aria-label="Предыдущая страница"
                @click.stop="readPage -= 1"
              >
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="caption grey--text text--darken-1">
                {{ readPage }} / {{ readPageCount }}
              </span>
              <v-btn
                icon
                x-small
                :disabled="readPage >= readPageCount"
                aria-label="Следующая страница"
                @click.stop="readPage += 1"
              >
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
        <div v-else-if="!activeJobs.length" class="lk-notifications-panel__empty">
          <v-icon size="48" color="grey lighten-1">mdi-bell-off-outline</v-icon>
          <p class="mt-3 mb-0 grey--text">Нет уведомлений</p>
        </div>
      </div>
    </v-card>
  </v-menu>

  <v-dialog v-model="cancelDialog" max-width="440">
    <v-card>
      <v-card-title class="text-h6">Отменить загрузку?</v-card-title>
      <v-card-text>
        Будет снята только выбранная задача
        <strong v-if="jobToCancel">{{ jobToCancel.mass_upload_id }}</strong>.
        Остальные задания в очереди продолжат выполняться.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text class="text-none" @click="cancelDialog = false">
          Нет
        </v-btn>
        <v-btn
          color="error"
          class="text-none"
          :loading="jobToCancel && !!cancellingJobs[jobToCancel.mass_upload_id]"
          @click="confirmCancelJob"
        >
          Отменить задачу
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import {
  getNotificationTypeAvatarStyle,
  getNotificationTypeChipColor,
  getNotificationTypeColor,
  getNotificationTypeIcon,
  getNotificationTypeLabel,
  getNotificationTypeUnreadStyle,
} from '@/constants/lk-notifications';

const READ_PER_PAGE = 4;

export default {
  name: 'LkNotificationsBell',
  mixins: [ruDateFormatMixin],
  data () {
    return {
      menuOpen: false,
      markingAll: false,
      shakeActive: false,
      shakeTimer: null,
      pollTimer: null,
      readPage: 1,
      cancellingJobs: {},
      cancelDialog: false,
      jobToCancel: null,
    };
  },
  computed: {
    ...mapGetters({
      items: 'LkNotifications/items',
      unreadCount: 'LkNotifications/unreadCount',
      loading: 'LkNotifications/loading',
      unreadItems: 'LkNotifications/unreadItems',
      readItems: 'LkNotifications/readItems',
      activeAudioJobs: 'MassAudioUploadJobs/activeJobs',
      activeLcdJobs: 'MassLcdUploadJobs/activeJobs',
    }),
    activeJobs () {
      const audio = (this.activeAudioJobs || []).map((job) => ({
        ...job,
        mediaKind: 'audio',
      }));
      const lcd = (this.activeLcdJobs || []).map((job) => ({
        ...job,
        mediaKind: 'lcd',
      }));
      return [...audio, ...lcd];
    },
    badgeCount () {
      return this.unreadCount + this.activeJobs.length;
    },
    readPageCount () {
      return Math.max(1, Math.ceil(this.readItems.length / READ_PER_PAGE));
    },
    paginatedReadItems () {
      const start = (this.readPage - 1) * READ_PER_PAGE;
      return this.readItems.slice(start, start + READ_PER_PAGE);
    },
  },
  mounted () {
    this.fetchUnreadCount();
    this.fetchAllActiveJobs().then((jobs) => {
      if (jobs && jobs.length) {
        this.startJobPolling();
      }
    }).catch(() => {});
    this.pollTimer = setInterval(() => {
      if (!this.menuOpen) {
        this.fetchUnreadCount();
        this.fetchAllActiveJobs().then((jobs) => {
          if (jobs && jobs.length) {
            this.startJobPolling();
          }
        }).catch(() => {});
      }
    }, 120000);
    this.startShakeInterval();
  },
  beforeDestroy () {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.stopShakeInterval();
    this.stopJobPolling();
  },
  watch: {
    unreadCount (count) {
      if (count > 0 || this.activeJobs.length > 0) {
        this.startShakeInterval();
      } else {
        this.stopShakeInterval();
        this.shakeActive = false;
      }
    },
    activeJobs (jobs, prevJobs) {
      if (jobs.length > 0) {
        this.startShakeInterval();
        this.startJobPolling();
        return;
      }
      if (prevJobs && prevJobs.length > 0) {
        this.refreshAfterJobComplete();
      }
    },
    readItems () {
      if (this.readPage > this.readPageCount) {
        this.readPage = this.readPageCount;
      }
    },
    menuOpen (open) {
      if (!open) {
        this.readPage = 1;
      }
    },
  },
  methods: {
    ...mapActions({
      fetchUnreadCount: 'LkNotifications/fetchUnreadCount',
      fetchList: 'LkNotifications/fetchList',
      markRead: 'LkNotifications/markRead',
      markAllReadAction: 'LkNotifications/markAllRead',
      fetchAudioActiveJobs: 'MassAudioUploadJobs/fetchActive',
      startAudioJobPolling: 'MassAudioUploadJobs/startPolling',
      stopAudioJobPolling: 'MassAudioUploadJobs/stopPolling',
      cancelAudioJobAction: 'MassAudioUploadJobs/cancelJob',
      fetchLcdActiveJobs: 'MassLcdUploadJobs/fetchActive',
      startLcdJobPolling: 'MassLcdUploadJobs/startPolling',
      stopLcdJobPolling: 'MassLcdUploadJobs/stopPolling',
      cancelLcdJobAction: 'MassLcdUploadJobs/cancelJob',
      exportResult: 'MassAudioUploadJobs/exportResult',
      downloadAudioFile: 'MassAudioUploadJobs/downloadAudioFile',
    }),
    jobTitle (job) {
      return job.mediaKind === 'lcd' ? 'Загрузка изображения' : 'Загрузка аудио';
    },
    fetchAllActiveJobs () {
      return Promise.all([
        this.fetchAudioActiveJobs().catch(() => []),
        this.fetchLcdActiveJobs().catch(() => []),
      ]).then(([audioJobs, lcdJobs]) => [
        ...(audioJobs || []),
        ...(lcdJobs || []),
      ]);
    },
    startJobPolling () {
      this.startAudioJobPolling();
      this.startLcdJobPolling();
    },
    stopJobPolling () {
      this.stopAudioJobPolling();
      this.stopLcdJobPolling();
    },
    onMenuToggle (open) {
      if (open) {
        this.fetchList({ limit: 50 }).catch(() => {
          this.$flash_warning('Не удалось загрузить уведомления');
        });
        this.fetchAllActiveJobs().then((jobs) => {
          if (jobs && jobs.length) {
            this.startJobPolling();
          }
        }).catch(() => {});
      }
    },
    refreshAfterJobComplete () {
      this.fetchUnreadCount().catch(() => {});
      if (this.menuOpen) {
        this.fetchList({ limit: 50 }).catch(() => {});
      }
    },
    startShakeInterval () {
      if (this.shakeTimer || (this.unreadCount <= 0 && this.activeJobs.length <= 0)) {
        return;
      }
      this.shakeTimer = setInterval(() => {
        if (this.unreadCount > 0 || this.activeJobs.length > 0) {
          this.triggerShake();
        }
      }, 5000);
    },
    stopShakeInterval () {
      if (this.shakeTimer) {
        clearInterval(this.shakeTimer);
        this.shakeTimer = null;
      }
    },
    triggerShake () {
      this.shakeActive = false;
      this.$nextTick(() => {
        this.shakeActive = true;
        setTimeout(() => {
          this.shakeActive = false;
        }, 600);
      });
    },
    typeIcon (type) {
      return getNotificationTypeIcon(type);
    },
    typeColor (type) {
      return getNotificationTypeColor(type);
    },
    typeLabel (type) {
      return getNotificationTypeLabel(type);
    },
    typeChipColor (type) {
      return getNotificationTypeChipColor(type);
    },
    typeAvatarStyle (type) {
      return getNotificationTypeAvatarStyle(type);
    },
    unreadCardStyle (type) {
      return getNotificationTypeUnreadStyle(type);
    },
    formatDate (value) {
      if (!value) return '';
      return this.ruDateFormat(value);
    },
    massUploadMetaFromBody (body) {
      if (!body) return null;
      const match = String(body).match(/\[(mass_upload_id|mass_lcd_upload_id):([^\]]+)\]/);
      if (!match) return null;
      return {
        id: match[2],
        kind: match[1] === 'mass_lcd_upload_id' ? 'lcd' : 'audio',
      };
    },
    massUploadIdFromBody (body) {
      const meta = this.massUploadMetaFromBody(body);
      return meta ? meta.id : null;
    },
    showMassUploadActions (item) {
      const title = String(item?.title || '').toLowerCase();
      if (title.includes('запущена') || title.includes('в очереди')) {
        return false;
      }
      return !!this.massUploadIdFromBody(item?.body);
    },
    displayBody (body) {
      if (!body) return '';
      return String(body).replace(/\n?\[(?:mass_upload_id|mass_lcd_upload_id):[^\]]+\]/, '').trim();
    },
    openMassResult (massUploadId, kind = 'audio') {
      if (!massUploadId) return;
      this.menuOpen = false;
      window.dispatchEvent(new CustomEvent('mass-audio-open-result', {
        detail: { massUploadId, mediaType: kind },
      }));
    },
    downloadMassResult (massUploadId) {
      this.exportResult(massUploadId).catch(() => {
        this.$flash_warning('Не удалось скачать результат');
      });
    },
    downloadMassAudio (massUploadId, kind = 'audio') {
      this.downloadAudioFile(massUploadId).catch(() => {
        this.$flash_warning(kind === 'lcd' ? 'Не удалось скачать изображение' : 'Не удалось скачать аудиофайл');
      });
    },
    openNotification (item) {
      if (!item.is_read) {
        this.markRead(item.id).catch(() => {
          this.$flash_warning('Не удалось отметить уведомление прочитанным');
        });
      }
    },
    askCancelJob (job) {
      this.jobToCancel = job;
      this.cancelDialog = true;
    },
    confirmCancelJob () {
      const job = this.jobToCancel;
      if (!job || !job.mass_upload_id) return;
      this.$set(this.cancellingJobs, job.mass_upload_id, true);
      const cancel = job.mediaKind === 'lcd'
        ? this.cancelLcdJobAction(job.mass_upload_id)
        : this.cancelAudioJobAction(job.mass_upload_id);
      cancel
        .then(() => {
          this.$flash_success('Задача снята с очереди');
          this.cancelDialog = false;
          this.jobToCancel = null;
        })
        .catch((e) => {
          const message = e?.response?.data?.message;
          this.$flash_warning(message || 'Не удалось отменить задачу');
        })
        .finally(() => {
          this.$set(this.cancellingJobs, job.mass_upload_id, false);
        });
    },
    markAllRead () {
      this.markingAll = true;
      this.markAllReadAction()
        .then(() => {
          this.$flash_success('Все уведомления прочитаны');
        })
        .catch(() => {
          this.$flash_warning('Не удалось отметить уведомления');
        })
        .finally(() => {
          this.markingAll = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.lk-notifications-bell__icon--shake {
  animation: lk-bell-shake 0.55s ease-in-out;
  transform-origin: top center;
}

@keyframes lk-bell-shake {
  0%, 100% { transform: rotate(0deg); }
  15% { transform: rotate(-12deg); }
  30% { transform: rotate(10deg); }
  45% { transform: rotate(-8deg); }
  60% { transform: rotate(6deg); }
  75% { transform: rotate(-4deg); }
}

.lk-notifications-panel {
  border-radius: 12px !important;
  overflow: hidden;
}

.lk-notifications-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(180deg, rgba(255, 193, 7, 0.12) 0%, transparent 100%);
}

.lk-notifications-panel__read-all {
  font-size: 0.75rem !important;
  letter-spacing: 0;
}

.lk-notifications-panel__body {
  overflow-y: auto;
  max-height: 420px;
}

.lk-notifications-panel__loader,
.lk-notifications-panel__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

.lk-notifications-panel__section-title {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.5);
  padding: 10px 16px 6px;
  background: #fafafa;

  .v-icon {
    color: #f44336;
  }
}

.lk-notifications-panel__section-count {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  margin-left: 4px;
}

.lk-notifications-panel__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px 12px;
  background: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.lk-notifications-panel__card {
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(13, 64, 146, 0.05);
  }

  &--unread {
    transition: background 0.25s ease, box-shadow 0.15s ease, filter 0.15s ease;

    &:hover {
      filter: brightness(0.98);
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
    }
  }
}

.lk-notification-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.lk-notification-card__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.lk-notification-card__body {
  flex: 1;
  min-width: 0;
}

.lk-notification-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.lk-notification-card__title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #263238;
  line-height: 1.35;
  word-break: break-word;
}

.lk-notification-card__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.lk-notification-card__type {
  height: 20px !important;
  font-size: 0.65rem !important;
}

.lk-notification-card__text {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.45;
  margin-top: 6px;
  word-break: break-word;
  white-space: pre-line;
}

.lk-job-card {
  padding: 4px 0;
}
.lk-job-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.lk-job-card__title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #263238;
}
.lk-job-card__cancel {
  flex-shrink: 0;
  margin-top: -4px;
}
.lk-notifications-panel__card--job {
  cursor: default;
  background: rgba(21, 101, 192, 0.04);
}
</style>
